import React, { useState, useEffect } from 'react';
import { Heart, CreditCard, DollarSign, CheckCircle, Loader2, AlertCircle, Utensils, BookOpen, HeartHandshake, Briefcase, Home, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Donate = () => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [donationType, setDonationType] = useState('one-time');
  const [designation, setDesignation] = useState('Where Needed Most');
  const [designations, setDesignations] = useState(['Where Needed Most']);
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCancelled, setShowCancelled] = useState(false);
  const [donationDetails, setDonationDetails] = useState(null);
  
  // Conference Registration specific fields
  const [conferenceFields, setConferenceFields] = useState({
    address: '',
    phone: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    tshirtSize: ''
  });
  
  // Check if conference registration is selected
  const isConferenceRegistration = designation.toLowerCase().includes('conference') || designation.toLowerCase().includes('arga');
  
  // T-shirt size options
  const tshirtSizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL'];

  // Impact cards with icons
  const impactCards = [
    { amount: 25, icon: Utensils, description: 'Feeds a family for a week' },
    { amount: 50, icon: BookOpen, description: 'Provides educational materials for a child' },
    { amount: 100, icon: HeartHandshake, description: 'Offers counseling session for someone in crisis' },
    { amount: 250, icon: Briefcase, description: 'Supports job training for one participant' },
    { amount: 500, icon: Home, description: 'Provides one month of transitional housing' },
  ];

  useEffect(() => {
    fetchDesignations();
    checkPaymentReturn();
  }, []);

  const fetchDesignations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/donations/programs`);
      if (response.data.designations) {
        setDesignations(response.data.designations);
      }
    } catch (error) {
      // Designations fetch failed - non-critical
    }
  };

  const checkPaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      setIsPolling(true);
      await pollPaymentStatus(sessionId);
      window.history.replaceState({}, document.title, '/donate');
    } else if (paymentStatus === 'cancelled') {
      setShowCancelled(true);
      window.history.replaceState({}, document.title, '/donate');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setIsPolling(false);
      toast.info('Payment status check timed out. Please check your email for confirmation.');
      setShowSuccess(true);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/donations/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setIsPolling(false);
        setDonationDetails({
          amount: (data.amount_total / 100).toFixed(2),
          currency: data.currency?.toUpperCase() || 'USD'
        });
        setShowSuccess(true);
        toast.success('Thank you for your generous donation!');
        return;
      } else if (data.status === 'expired') {
        setIsPolling(false);
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      setIsPolling(false);
      setShowSuccess(true);
    }
  };

  const getDonationAmount = () => {
    if (customAmount) return parseFloat(customAmount);
    return selectedAmount || 0;
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    setCustomAmount(amount.toString());
  };

  const handleDonate = async () => {
    const amount = getDonationAmount();
    
    if (!amount || amount < 1) {
      toast.error('Please select or enter a donation amount (minimum $1)');
      return;
    }
    
    if (!donorName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    
    if (!donorEmail.trim() || !donorEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Additional validation for conference registration
    if (isConferenceRegistration) {
      if (!conferenceFields.address.trim()) {
        toast.error('Please enter your address');
        return;
      }
      if (!conferenceFields.phone.trim()) {
        toast.error('Please enter your phone number');
        return;
      }
      if (!conferenceFields.emergencyContactName.trim()) {
        toast.error('Please enter an emergency contact name');
        return;
      }
      if (!conferenceFields.emergencyContactPhone.trim()) {
        toast.error('Please enter an emergency contact phone number');
        return;
      }
      if (!conferenceFields.tshirtSize) {
        toast.error('Please select a T-shirt size');
        return;
      }
    }

    setIsProcessing(true);

    try {
      // Build request payload
      const payload = {
        amount: parseFloat(amount),
        donor_name: donorName.trim(),
        donor_email: donorEmail.trim(),
        designation: designation,
        donation_type: donationType,
        origin_url: window.location.origin
      };
      
      // Add conference registration fields if applicable
      if (isConferenceRegistration) {
        payload.conference_registration = {
          address: conferenceFields.address.trim(),
          phone: conferenceFields.phone.trim(),
          emergency_contact_name: conferenceFields.emergencyContactName.trim(),
          emergency_contact_phone: conferenceFields.emergencyContactPhone.trim(),
          tshirt_size: conferenceFields.tshirtSize
        };
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/donations/checkout/create`, payload);

      if (response.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process donation. Please try again.');
      setIsProcessing(false);
    }
  };

  // Success View
  if (showSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F5DC]/30 to-white py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-12 pb-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#0A345E] mb-4">Thank You!</h1>
              <p className="text-xl text-gray-600 mb-4">
                Your generous donation has been received.
              </p>
              {donationDetails && (
                <div className="bg-green-50 rounded-lg p-4 mb-6 inline-block">
                  <p className="text-2xl font-bold text-green-700">
                    ${donationDetails.amount} {donationDetails.currency}
                  </p>
                </div>
              )}
              <p className="text-gray-600 mb-8 max-w-lg mx-auto">
                Thank you for choosing to become a financial partner of Gateway Inc. With your support we get the transformational opportunity to tell the story of Jesus and actively serve and empower individuals and families in our community and abroad. A confirmation email will be sent to you shortly.
              </p>
              <Button 
                onClick={() => {
                  setShowSuccess(false);
                  setDonationDetails(null);
                  setSelectedAmount(null);
                  setCustomAmount('');
                  setDonorName('');
                  setDonorEmail('');
                }}
                className="bg-[#007A7A] hover:bg-[#006666]"
              >
                Make Another Donation
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Cancelled View
  if (showCancelled) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F5DC]/30 to-white py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-12 pb-8 text-center">
              <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-amber-600" />
              </div>
              <h1 className="text-3xl font-bold text-[#0A345E] mb-4">Payment Cancelled</h1>
              <p className="text-xl text-gray-600 mb-8">
                Your payment was not processed. No charges have been made to your account.
              </p>
              <Button 
                onClick={() => setShowCancelled(false)}
                className="bg-[#007A7A] hover:bg-[#006666]"
              >
                Return to Donation Form
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Polling View
  if (isPolling) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F5DC]/30 to-white py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="border-0 shadow-xl">
            <CardContent className="pt-12 pb-8 text-center">
              <Loader2 className="w-16 h-16 text-[#007A7A] animate-spin mx-auto mb-6" />
              <h1 className="text-2xl font-bold text-[#0A345E] mb-4">Processing Payment...</h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F5DC]/30 to-white">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-r from-[#0A345E] to-[#007A7A]">
        <div className="container mx-auto px-4 text-center">
          <Heart className="w-16 h-16 text-white/90 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Make a Difference Today</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Your generosity helps build brighter futures for individuals and families through economic and community development.
          </p>
        </div>
      </section>

      {/* Your Impact Section - ABOVE the form */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#0A345E] mb-8">Your Impact</h2>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {impactCards.map((item) => {
              const IconComponent = item.icon;
              return (
                <Card 
                  key={item.amount} 
                  className="text-center border-0 shadow-md cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleAmountSelect(item.amount)}
                >
                  <CardContent className="pt-6 pb-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#0A345E] to-[#007A7A] rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-3xl font-bold text-[#007A7A] mb-2">${item.amount}</div>
                    <p className="text-sm text-gray-600">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <Card className="border-0 shadow-xl">
            <CardHeader className="text-center pb-2">
              <CardTitle className="text-2xl text-[#0A345E]">Choose Your Donation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 pt-4">
              
              {/* Donation Type Toggle */}
              <div className="flex justify-center">
                <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
                  <button
                    onClick={() => setDonationType('one-time')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      donationType === 'one-time'
                        ? 'bg-[#007A7A] text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    One-Time
                  </button>
                  <button
                    onClick={() => setDonationType('monthly')}
                    className={`px-6 py-2 rounded-md text-sm font-medium transition-all ${
                      donationType === 'monthly'
                        ? 'bg-[#007A7A] text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Amount
                </label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    placeholder="Enter amount"
                    value={customAmount}
                    onChange={(e) => {
                      setCustomAmount(e.target.value);
                      const val = parseFloat(e.target.value);
                      if (impactCards.some(c => c.amount === val)) {
                        setSelectedAmount(val);
                      } else {
                        setSelectedAmount(null);
                      }
                    }}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent text-lg"
                  />
                </div>
              </div>

              {/* Designation Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Designation
                </label>
                <select
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent bg-white"
                >
                  {designations.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Donor Information */}
              <div className="space-y-4 pt-4 border-t border-gray-100">
                <h3 className="text-lg font-semibold text-[#0A345E]">Your Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Enter your full name"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#007A7A] focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Conference Registration Additional Fields */}
              {isConferenceRegistration && (
                <div className="space-y-4 pt-4 border-t border-gray-100 bg-purple-50 -mx-6 px-6 py-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-[#3d1f4f]">Conference Registration Details</h3>
                  <p className="text-sm text-gray-600 mb-4">Please complete all fields below for your ARGA Conference registration.</p>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mailing Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Street address, City, State, ZIP"
                      value={conferenceFields.address}
                      onChange={(e) => setConferenceFields({...conferenceFields, address: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d1f4f] focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      placeholder="(555) 555-5555"
                      value={conferenceFields.phone}
                      onChange={(e) => setConferenceFields({...conferenceFields, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d1f4f] focus:border-transparent"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emergency Contact Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        placeholder="Emergency contact full name"
                        value={conferenceFields.emergencyContactName}
                        onChange={(e) => setConferenceFields({...conferenceFields, emergencyContactName: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d1f4f] focus:border-transparent"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Emergency Contact Phone <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        placeholder="(555) 555-5555"
                        value={conferenceFields.emergencyContactPhone}
                        onChange={(e) => setConferenceFields({...conferenceFields, emergencyContactPhone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d1f4f] focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      T-Shirt Size <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={conferenceFields.tshirtSize}
                      onChange={(e) => setConferenceFields({...conferenceFields, tshirtSize: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#3d1f4f] focus:border-transparent bg-white"
                      required
                    >
                      <option value="">Select T-Shirt Size</option>
                      {tshirtSizes.map((size) => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {/* Donate Button */}
              <Button
                onClick={handleDonate}
                disabled={isProcessing || !customAmount}
                className="w-full py-6 text-lg bg-[#007A7A] hover:bg-[#006666] disabled:bg-gray-300"
                data-testid="donate-button"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Donate ${getDonationAmount() || '0'} {donationType === 'monthly' ? '/month' : ''}
                  </>
                )}
              </Button>

            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Badges - Three Check Marks */}
      <section className="py-12 bg-[#F5F5DC]/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5D5C5] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#4A6741]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A345E] mb-2">Secure</h3>
              <p className="text-gray-600">SSL encrypted transactions</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5D5C5] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#4A6741]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A345E] mb-2">Tax-Deductible</h3>
              <p className="text-gray-600">501(c)(3) certified</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#C5D5C5] rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#4A6741]" />
              </div>
              <h3 className="text-xl font-bold text-[#0A345E] mb-2">Transparent</h3>
              <p className="text-gray-600">Track your impact</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
